import React, { useState, useEffect, useRef } from "react";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../lib/supabaseConfig";
import { useNavigate, Link } from "react-router-dom";
import { REDIRECT_URL } from "../utils/constants";
import { gsap } from "gsap";
import {
  Mail,
  ArrowLeft,
  Shield,
  Loader2,
  CheckCircle,
  Users,
  TrendingUp,
  Star,
} from "lucide-react";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [gridDimensions, setGridDimensions] = useState({ rows: 0, cols: 0 });
  const navigate = useNavigate();

  // Refs for animations
  const containerRef = useRef(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    // Set grid dimensions after component mounts
    const updateGridDimensions = () => {
      setGridDimensions({
        rows: Math.ceil(window.innerHeight / 40),
        cols: Math.ceil(window.innerWidth / 40),
      });
    };

    updateGridDimensions();
    window.addEventListener("resize", updateGridDimensions);

    return () => window.removeEventListener("resize", updateGridDimensions);
  }, []);

  useEffect(() => {
    // Initial animations
    const tl = gsap.timeline();

    // Set initial states
    gsap.set([leftPanelRef.current, rightPanelRef.current], {
      opacity: 0,
      x: -50,
    });

    gsap.set(rightPanelRef.current, {
      x: 50,
    });

    // Animate in
    tl.to([leftPanelRef.current, rightPanelRef.current], {
      opacity: 1,
      x: 0,
      duration: 0.8,
      ease: "power3.out",
      stagger: 0.2,
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      // The reset token is generated, stored, and emailed entirely server-side
      // by the edge function (it holds the service key). The browser never
      // receives the token, so it can't be used to hijack the account.
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/send-verification-email`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email,
            username: email.split("@")[0], // Use email prefix as username
            action: "password_reset",
          }),
        }
      );

      if (!response.ok) {
        // Log for diagnostics but never reveal whether the account exists.
        console.error("Password reset request failed:", response.status);
      }

      // Always show the same generic message regardless of whether the account
      // exists, so this endpoint can't be used to enumerate registered emails.
      setSuccess(
        "If an account with this email exists, you'll receive a password reset link shortly."
      );

      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      console.error("Password reset error:", err);
      setError(err.message || "Failed to process reset request");
    }
    setLoading(false);
  };

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center relative overflow-hidden"
    >
      {/* Grid background with center glow effect */}
      <div className="absolute inset-0">
        {/* Base grid pattern */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
          }}
        />

        {/* Radial gradient mask to create center glow and edge fade effect */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(circle at 50% 50%, 
                rgba(184, 70, 14, 0.15) 0%, 
                rgba(184, 70, 14, 0.08) 40%, 
                rgba(184, 70, 14, 0.03) 70%,
                rgba(184, 70, 14, 0) 100%
              )
            `,
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
            maskImage: `
              radial-gradient(circle at 50% 50%, 
                rgba(255,255,255,1) 0%, 
                rgba(255,255,255,0.8) 30%, 
                rgba(255,255,255,0.3) 60%,
                rgba(255,255,255,0.1) 80%,
                rgba(255,255,255,0) 100%
              )
            `,
            WebkitMaskImage: `
              radial-gradient(circle at 50% 50%, 
                rgba(255,255,255,1) 0%, 
                rgba(255,255,255,0.8) 30%, 
                rgba(255,255,255,0.3) 60%,
                rgba(255,255,255,0.1) 80%,
                rgba(255,255,255,0) 100%
              )
            `,
          }}
        />

        {/* Interactive grid overlay for hover effects */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: gridDimensions.rows }).map((_, row) =>
            Array.from({ length: gridDimensions.cols }).map((_, col) => (
              <div
                key={`${row}-${col}`}
                className="absolute w-10 h-10 pointer-events-auto transition-all duration-300"
                style={{
                  left: `${col * 40}px`,
                  top: `${row * 40}px`,
                  animation: "none",
                }}
                onMouseEnter={(e) => {
                  gsap.fromTo(
                    e.target,
                    {
                      backgroundColor: "rgba(184, 70, 14, 0)",
                      boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                      scale: 1,
                    },
                    {
                      backgroundColor: "rgba(184, 70, 14, 0.3)",
                      boxShadow:
                        "0 0 25px rgba(184, 70, 14, 0.8), inset 0 0 15px rgba(184, 70, 14, 0.3)",
                      scale: 1.05,
                      duration: 0.2,
                      ease: "power2.out",
                    }
                  );

                  const adjacentSquares = document.querySelectorAll(
                    `[data-row="${row}"][data-col="${
                      col - 1
                    }"], [data-row="${row}"][data-col="${
                      col + 1
                    }"], [data-row="${
                      row - 1
                    }"][data-col="${col}"], [data-row="${
                      row + 1
                    }"][data-col="${col}"]`
                  );
                  adjacentSquares.forEach((square, index) => {
                    gsap.fromTo(
                      square,
                      {
                        backgroundColor: "rgba(184, 70, 14, 0)",
                        boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                      },
                      {
                        backgroundColor: "rgba(184, 70, 14, 0.1)",
                        boxShadow: "0 0 10px rgba(184, 70, 14, 0.4)",
                        duration: 0.15,
                        delay: index * 0.05,
                        ease: "power2.out",
                        onComplete: () => {
                          gsap.to(square, {
                            backgroundColor: "rgba(184, 70, 14, 0)",
                            boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                            duration: 0.3,
                            ease: "power2.out",
                          });
                        },
                      }
                    );
                  });
                }}
                onMouseLeave={(e) => {
                  gsap.to(e.target, {
                    backgroundColor: "rgba(184, 70, 14, 0)",
                    boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                    scale: 1,
                    duration: 0.4,
                    ease: "power2.out",
                  });
                }}
                data-row={row}
                data-col={col}
              />
            ))
          )}
        </div>
      </div>

      {/* Main Centered Container */}
      <div className="relative z-10 w-full max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl lg:rounded-3xl shadow-2xl overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Left Panel - Reset Form */}
            <div
              ref={leftPanelRef}
              className="w-full lg:w-1/2 p-4 sm:p-6 lg:p-8 flex items-center justify-center order-2 lg:order-1"
            >
              <div className="w-full max-w-sm">
                <div ref={formRef}>
                  {/* Logo */}
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-[#B8460E] via-[#8B3A0E] to-[#B8460E] rounded-2xl flex items-center justify-center shadow-lg">
                      <Shield className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
                    </div>
                  </div>

                  {/* Title */}
                  <div className="text-center mb-6">
                    <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">
                      Forgot Password
                    </h1>
                    <p className="text-sm lg:text-base text-gray-300">
                      Enter your email to reset your password
                    </p>
                  </div>

                  {/* Reset Form */}
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                      <div className="bg-red-900/20 border border-red-500/50 text-red-300 px-4 py-2.5 rounded-xl text-sm">
                        {error}
                      </div>
                    )}

                    {success && (
                      <div className="bg-green-900/20 border border-green-500/50 text-green-300 px-4 py-2.5 rounded-xl text-sm flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                        {success}
                      </div>
                    )}

                    {/* Email Input */}
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-300 mb-1.5"
                      >
                        Email address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl pl-10 pr-4 py-2.5 lg:py-3 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] focus:ring-2 focus:ring-[#B8460E]/20 transition-all duration-200"
                          placeholder="Enter your email address"
                          required
                          autoFocus
                          disabled={loading}
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-[#B8460E] via-[#8B3A0E] to-[#B8460E] hover:from-[#D4541A] hover:via-[#B8460E] hover:to-[#D4541A] disabled:opacity-50 disabled:cursor-not-allowed text-white py-2.5 lg:py-3 px-6 rounded-xl transition-all duration-200 font-semibold flex items-center justify-center text-sm lg:text-base shadow-lg hover:shadow-xl"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending Reset Email...
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          Send Reset Email
                        </>
                      )}
                    </button>

                    {/* Back to Login */}
                    <div className="text-center">
                      <Link
                        to="/login"
                        className="inline-flex items-center text-sm text-gray-300 hover:text-[#B8460E] transition-colors"
                      >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back to Login
                      </Link>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Right Panel - Features/Info */}
            <div
              ref={rightPanelRef}
              className="w-full lg:w-1/2 bg-gradient-to-br from-[#B8460E] via-[#8B3A0E] to-[#B8460E] p-4 sm:p-6 lg:p-8 flex items-center justify-center order-1 lg:order-2"
            >
              {/* Background decoration */}
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="absolute -top-40 -left-40 w-80 h-80 bg-white/10 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>

              <div className="relative z-10 text-white text-center max-w-md">
                <div className="mb-6">
                  <div className="w-16 h-16 lg:w-20 lg:h-20 bg-white/20 rounded-3xl mx-auto mb-4 flex items-center justify-center shadow-2xl backdrop-blur-sm">
                    <Mail className="w-8 h-8 lg:w-10 lg:h-10 text-white" />
                  </div>
                  <h2 className="text-xl lg:text-2xl font-bold text-white mb-2">
                    Reset Your Password
                  </h2>
                  <p className="text-sm lg:text-base text-orange-100 leading-relaxed">
                    Don't worry! It happens to the best of us. Enter your email
                    and we'll send you a link to reset your password.
                  </p>
                </div>

                {/* Feature Points */}
                <div className="space-y-3 text-left">
                  <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-xl p-2.5">
                    <div className="w-2 h-2 bg-orange-300 rounded-full mr-3"></div>
                    <span className="text-sm text-orange-100">
                      Secure password reset process
                    </span>
                  </div>
                  <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-xl p-2.5">
                    <div className="w-2 h-2 bg-orange-300 rounded-full mr-3"></div>
                    <span className="text-sm text-orange-100">
                      Link expires in 24 hours for security
                    </span>
                  </div>
                  <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-xl p-2.5">
                    <div className="w-2 h-2 bg-orange-300 rounded-full mr-3"></div>
                    <span className="text-sm text-orange-100">
                      Get back to your dashboard quickly
                    </span>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 mt-8 pt-6 border-t border-white/20">
                  <div className="text-center">
                    <div className="flex items-center justify-center w-8 h-8 bg-white/20 rounded-lg mx-auto mb-1">
                      <Users className="w-4 h-4 text-orange-300" />
                    </div>
                    <div className="text-xs text-orange-200">
                      <span className="font-semibold text-white">10K+</span>
                      <br />
                      Users
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center w-8 h-8 bg-white/20 rounded-lg mx-auto mb-1">
                      <TrendingUp className="w-4 h-4 text-orange-300" />
                    </div>
                    <div className="text-xs text-orange-200">
                      <span className="font-semibold text-white">99.9%</span>
                      <br />
                      Uptime
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center w-8 h-8 bg-white/20 rounded-lg mx-auto mb-1">
                      <Star className="w-4 h-4 text-orange-300" />
                    </div>
                    <div className="text-xs text-orange-200">
                      <span className="font-semibold text-white">4.9</span>
                      <br />
                      Rating
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
